import React, { useId, useState } from 'react';
import { CHART_COLORS } from './reporting/chartUtils.js';
import { compactNumber, fmt, niceCeiling } from '../lib/usageTable.js';
import { monthLabel } from '../lib/usage.js';

const WIDTH = 560;
const HEIGHT = 200;
const PLOT = { top: 12, right: 8, bottom: 36, left: 44 };
const BAR_COLOR = CHART_COLORS[0];
const BASELINE_OVERHANG = 16;

function describe(point, unit) {
  if (point.value === null) return `${monthLabel(point.month)}: not computed`;
  const partial = point.complete === false ? ' (partial month)' : '';
  return `${monthLabel(point.month)}: ${fmt(point.value)} ${unit}${partial}`;
}

export function MonthlyBarChart({ title, unit, points }) {
  const [active, setActive] = useState(null);
  const clipId = useId();
  const plotWidth = WIDTH - PLOT.left - PLOT.right;
  const plotHeight = HEIGHT - PLOT.top - PLOT.bottom;
  const max = niceCeiling(Math.max(0, ...points.map((p) => p.value || 0)));
  const slot = plotWidth / points.length;
  const barWidth = Math.min(28, slot * 0.6);
  const yOf = (value) => PLOT.top + plotHeight - (value / max) * plotHeight;
  const ticks = [0, max / 2, max];

  return (
    <figure className="monthly-chart">
      <figcaption>{title}</figcaption>
      <div className="monthly-chart-plot">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="img"
          aria-label={`${title} per month`}
        >
          <clipPath id={clipId}>
            <rect
              x={PLOT.left}
              y={PLOT.top}
              width={plotWidth}
              height={plotHeight}
            />
          </clipPath>
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                className="monthly-chart-grid"
                x1={PLOT.left}
                x2={WIDTH - PLOT.right}
                y1={yOf(tick)}
                y2={yOf(tick)}
              />
              <text
                className="monthly-chart-axis"
                x={PLOT.left - 8}
                y={yOf(tick) + 4}
                textAnchor="end"
              >
                {compactNumber(tick)}
              </text>
            </g>
          ))}
          {points.map((point, i) => {
            const center = PLOT.left + slot * i + slot / 2;
            const showYear = i === 0 || point.month.endsWith('-01');
            const height =
              point.value === null ? 0 : (point.value / max) * plotHeight;
            return (
              <g key={point.month}>
                <rect
                  className={`monthly-chart-hit${active === i ? ' is-active' : ''}`}
                  x={center - slot / 2}
                  y={PLOT.top}
                  width={slot}
                  height={plotHeight}
                  tabIndex={0}
                  aria-label={describe(point, unit)}
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(i)}
                  onBlur={() => setActive(null)}
                />
                {height > 0 && (
                  <rect
                    className="monthly-chart-bar"
                    x={center - barWidth / 2}
                    y={PLOT.top + plotHeight - height}
                    width={barWidth}
                    height={height + BASELINE_OVERHANG}
                    clipPath={`url(#${clipId})`}
                    fill={BAR_COLOR}
                    fillOpacity={point.complete === false ? 0.45 : 1}
                  />
                )}
                <text
                  className="monthly-chart-axis"
                  x={center}
                  y={HEIGHT - PLOT.bottom + 16}
                  textAnchor="middle"
                >
                  {new Date(`${point.month}-01T00:00:00Z`).toLocaleDateString(
                    'en-US',
                    { month: 'short', timeZone: 'UTC' },
                  )}
                </text>
                {showYear && (
                  <text
                    className="monthly-chart-axis"
                    x={center}
                    y={HEIGHT - PLOT.bottom + 30}
                    textAnchor="middle"
                  >
                    {point.month.slice(0, 4)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {active !== null && (
          <div
            className="monthly-chart-tooltip"
            role="tooltip"
            style={{
              left: `${((PLOT.left + slot * active + slot / 2) / WIDTH) * 100}%`,
            }}
          >
            {describe(points[active], unit)}
          </div>
        )}
      </div>
    </figure>
  );
}
